"use client"

import { useState, useEffect, useRef } from "react"
import { useParams, useRouter } from "next/navigation"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { BookOpen, Users, ArrowLeft, Sparkles, Star, TrendingUp, Eye } from "lucide-react"
import { Header } from "@/components/header"
import { R2Image } from "@/components/r2-image"
import Link from "next/link"
import { bookPublicPath } from "@/lib/book-url"
import { trackCollectionView } from "@/lib/analytics"

interface Book {
  id: string
  slug?: string | null
  title: string
  author: string
  description?: string
  coverUrl?: string
  genre?: string
  summary_5mins?: string
  summary_15mins?: string
  summary_chapter?: string
  podcastUrl?: string
}

interface Collection {
  id: string
  title: string
  description: string
  tagline?: string
  category: string
  isFeatured: boolean
  visitCount: number
  books: Array<{
    book: Book
  }>
  _count: {
    books: number
  }
}

export default function CollectionDetailPage() {
  const params = useParams()
  const router = useRouter()
  const [collection, setCollection] = useState<Collection | null>(null)
  const [loading, setLoading] = useState(true)
  const collectionTrackSentRef = useRef<string | null>(null)

  useEffect(() => {
    const fetchCollection = async () => {
      try {
        const response = await fetch(`/api/collections/${params.id}`)
        if (response.ok) {
          const data = await response.json()
          setCollection(data)
          
          // Track the collection visit
          try {
            await fetch(`/api/collections/${params.id}/visit`, {
              method: 'POST'
            })
          } catch (visitError) {
            console.error('Error tracking collection visit:', visitError)
            // Don't fail the page load if visit tracking fails
          }
        } else {
          router.push('/collections')
        }
      } catch (error) {
        console.error('Error fetching collection:', error)
        router.push('/collections')
      } finally {
        setLoading(false)
      }
    }

    if (params.id) {
      fetchCollection()
    }
  }, [params.id, router])

  useEffect(() => {
    if (!collection || !params.id) return
    if (collectionTrackSentRef.current === collection.id) return
    collectionTrackSentRef.current = collection.id
    trackCollectionView({
      collection_name: collection.title,
      collection_slug: String(params.id),
      book_count: collection._count.books,
    })
  }, [collection, params.id])

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'challenge': return <Sparkles className="h-5 w-5" />
      case 'inspiration': return <Users className="h-5 w-5" />
      case 'professional': return <TrendingUp className="h-5 w-5" />
      default: return <BookOpen className="h-5 w-5" />
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="relative overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 dark:from-violet-950/20 dark:via-purple-950/20 dark:to-fuchsia-950/20"></div>
          
          <div className="container mx-auto px-4 py-12 relative z-10">
            <div className="animate-pulse space-y-8">
              <div className="h-10 bg-muted rounded-xl w-1/4" />
              <div className="h-20 bg-muted rounded-xl w-3/4" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-80 bg-muted rounded-xl" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!collection) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Collection not found</h2>
            <Button onClick={() => router.push('/collections')}>
              Back to Collections
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Header with Stunning Background */}
      <div className="relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-100 via-purple-100 to-fuchsia-100 dark:from-violet-950/40 dark:via-purple-950/40 dark:to-fuchsia-950/40"></div>
        
        {/* Animated orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-purple-400/30 to-pink-400/30 dark:from-purple-600/30 dark:to-pink-600/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-violet-400/30 to-fuchsia-400/30 dark:from-violet-600/30 dark:to-fuchsia-600/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
          backgroundSize: '48px 48px'
        }}></div>

        <div className="container mx-auto px-4 py-16 relative z-10">
          <Button
            variant="ghost"
            onClick={() => router.push('/collections')}
            className="mb-8 hover:bg-white/50 dark:hover:bg-slate-800/50 backdrop-blur-sm rounded-xl"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Collections
          </Button>

          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              {collection.isFeatured && (
                <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 shadow-lg px-4 py-2">
                  <Star className="h-4 w-4 mr-1" />
                  Featured Collection
                </Badge>
              )}
              <Badge 
                variant="outline"
                className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-2 px-4 py-2"
              >
                {getCategoryIcon(collection.category)}
                <span className="ml-2 capitalize font-semibold">{collection.category}</span>
              </Badge>
              <Badge variant="outline" className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm px-4 py-2">
                <BookOpen className="h-4 w-4 mr-1" />
                {collection._count.books} Books
              </Badge>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-violet-600 dark:from-purple-400 dark:via-pink-400 dark:to-violet-400 bg-clip-text text-transparent">
              {collection.title}
            </h1>
            
            {collection.tagline && (
              <p className="text-2xl text-purple-700 dark:text-purple-300 mb-6 italic font-medium">
                &ldquo;{collection.tagline}&rdquo;
              </p>
            )}

            <p className="text-xl text-slate-700 dark:text-slate-300 leading-relaxed mb-8">
              {collection.description}
            </p>

            <div className="flex items-center gap-8 text-sm">
              <div className="flex items-center gap-2 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm px-4 py-2 rounded-full">
                <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                <span className="font-medium">Curated by Experts</span>
              </div>
              {collection.visitCount > 0 && (
                <div className="flex items-center gap-2 bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm px-4 py-2 rounded-full">
                  <TrendingUp className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                  <span className="font-medium">{collection.visitCount.toLocaleString()} visits</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Books Grid Section */}
      <div className="relative py-16 overflow-hidden">
        {/* Subtle background */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-slate-50/30 to-background dark:via-slate-900/30"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
              Books in this Collection
            </h2>
          </div>
          
          {collection.books.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {collection.books.map((item, index) => {
                const book = item.book
                return (
                  <Link key={book.id} href={bookPublicPath(book)}>
                    <Card 
                      className="group hover:shadow-2xl hover:shadow-purple-500/20 dark:hover:shadow-purple-500/30 hover:scale-[1.03] hover:-translate-y-2 transition-all duration-500 flex flex-col h-full cursor-pointer bg-gradient-to-br from-white via-white to-purple-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-purple-950/30 border-2 border-slate-200 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-700 overflow-hidden animate-fadeIn"
                      style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'both' }}
                    >
                      {/* Book Cover */}
                      <div className="aspect-[3/4] relative overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700">
                        {book.coverUrl ? (
                          <R2Image
                            src={book.coverUrl}
                            alt={book.title}
                            fill
                            className="object-contain object-center group-hover:scale-105 transition-transform duration-500"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                            quality={90}
                            fallbackIcon={<BookOpen className="h-12 w-12 text-muted-foreground" />}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="h-16 w-16 text-muted-foreground group-hover:text-purple-500 transition-colors duration-300" />
                          </div>
                        )}
                      </div>
                      
                      {/* Book Details */}
                      <CardContent className="p-5 flex-1 flex flex-col relative">
                        {/* Hover gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-purple-500/0 to-purple-500/0 group-hover:from-purple-500/5 group-hover:to-transparent transition-all duration-500"></div>
                        
                        <div className="relative z-10 flex-1 flex flex-col">
                          <h3 className="font-bold text-lg mb-2 line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                            {book.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            by {book.author}
                          </p>
                          
                          {book.description && (
                            <p className="text-xs text-muted-foreground mb-3 line-clamp-2 flex-1">
                              {book.description}
                            </p>
                          )}
                          
                          <div className="flex items-center justify-between mt-auto pt-3 border-t border-slate-200 dark:border-slate-700">
                            {book.genre && (
                              <div className="flex flex-wrap gap-1">
                                {book.genre.split(',').map((g, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs group-hover:bg-purple-100 dark:group-hover:bg-purple-900/30 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors">
                                    {g.trim()}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 group-hover:gap-2 transition-all duration-300">
                              <span className="font-semibold">View Details</span>
                              <Eye className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full mb-6">
                <BookOpen className="h-12 w-12 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
                No books in this collection yet
              </h3>
              <p className="text-muted-foreground text-lg mb-6">
                This collection is being curated. Check back soon!
              </p>
              <Button 
                onClick={() => router.push('/collections')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Browse Other Collections
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Add fadeIn animation */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  )
}
